import React from 'react';
import type { Climb } from '@/lib/climbData';
import Icon from '@/components/ui/AppIcon';

const difficultyLabel: Record<string, string> = {
  medium: 'Media',
  hard: 'Difficile',
  extreme: 'Estrema',
};

const difficultyClass: Record<string, string> = {
  medium: 'climb-difficulty-medium',
  hard: 'climb-difficulty-hard',
  extreme: 'climb-difficulty-extreme',
};

interface ClimbStatsHeaderProps {
  climb: Climb | undefined;
  participantCount: number;
  leaderTime: string;
  leaderName: string;
}

export default function ClimbStatsHeader({
  climb,
  participantCount,
  leaderTime,
  leaderName,
}: ClimbStatsHeaderProps) {
  if (!climb) {
    return (
      <div className="bg-card border border-border rounded-xl p-5 mb-6 card-glow">
        <div className="flex items-center justify-center py-4">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-5 mb-6 card-glow">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-2xl font-bold text-foreground">{climb.name}</h2>
            <span className={`text-sm font-semibold ${difficultyClass[climb.difficulty]}`}>
              ● {difficultyLabel[climb.difficulty]}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">{climb.location}</p>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2 bg-muted rounded-lg px-4 py-2.5">
            <Icon name="ArrowTrendingUpIcon" size={16} className="text-primary" />
            <div>
              <p className="text-xs text-muted-foreground font-medium">Dislivello</p>
              <p className="text-sm font-bold text-foreground font-mono-nums">+{climb.elevation.toLocaleString('it')} m</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-lg px-4 py-2.5">
            <Icon name="MapPinIcon" size={16} className="text-accent" />
            <div>
              <p className="text-xs text-muted-foreground font-medium">Distanza</p>
              <p className="text-sm font-bold text-foreground font-mono-nums">{climb.distance} km</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-lg px-4 py-2.5">
            <Icon name="ChartBarIcon" size={16} className="text-yellow-400" />
            <div>
              <p className="text-xs text-muted-foreground font-medium">Pendenza</p>
              <p className="text-sm font-bold text-foreground font-mono-nums">{climb.avgGradient}% avg / {climb.maxGradient}% max</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-lg px-4 py-2.5">
            <Icon name="UsersIcon" size={16} className="text-green-400" />
            <div>
              <p className="text-xs text-muted-foreground font-medium">Partecipanti</p>
              <p className="text-sm font-bold text-foreground font-mono-nums">{participantCount}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-muted rounded-lg px-4 py-2.5">
            <Icon name="TrophyIcon" size={16} className="text-yellow-400" />
            <div>
              <p className="text-xs text-muted-foreground font-medium">Record</p>
              <p className="text-sm font-bold text-foreground font-mono-nums">{leaderTime}</p>
              <p className="text-xs text-muted-foreground">{leaderName}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}